import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Row, Card, Tooltip, Typography, Col, Modal, Pagination } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  DragOutlined,
  LeftOutlined,
} from '@ant-design/icons';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import { useCategories } from '../../hooks/useMenuQuery';
import DndList from '../../components/DndList/DndList';

const { Meta } = Card;
const { Title, Text } = Typography;

function Categories({ history }) {
  const navigate = useNavigate();
  const { setHeaderComponent, isMobile } = useContext(LayoutContext);
  const [selectedItemDelete, setSelectedItemToDelete] = useState();
  const [sortMode, setSortMode] = useState(false);
  const [tempSortedList, setTempSortedList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Show 20 categories per page

  const { doFetch: fetchDelete } = useFetch();
  const { doFetch: fetchSort } = useFetch();
  const menuId = getMenuId();

  // Fetch categories with pagination
  const { data: categoriesData, isLoading: isLoadingCategories, isFetching: isFetchingCategories, error: categoriesError, refetch: refetchCategories } = useCategories(menuId, currentPage, pageSize);

  useEffect(() => {
    setHeaderComponent('Category List');

    return () => {
      setHeaderComponent(null);
    };
  }, []);

  const handleItemDelete = () => {
    fetchDelete({
      url: URLS.deleteCategory({
        categoryId: selectedItemDelete.id,
      }),
      method: 'DELETE',
      onSuccess: () => {
        refetchCategories();
        setSelectedItemToDelete();
      },
    });
  };

  const handleSortChange = newList => {
    setTempSortedList(newList);
    setHasUnsavedChanges(true);
  };

  const handleSaveSortOrder = () => {
    fetchSort({
      url: URLS.categoryPrioritiesUpdate,
      method: 'POST',
      params: {
        priorities: tempSortedList.map(cat => cat.id),
      },
      onSuccess: () => {
        setSortMode(false);
        setHasUnsavedChanges(false);
        setTempSortedList([]);
        refetchCategories();
      },
    });
  };

  const handleCancelSort = () => {
    setSortMode(false);
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSortMode(false); // Exit sort mode when changing pages
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  const enterSortMode = () => {
    setSortMode(true);
    // Transform categories to include translations property, just like in non-sort mode
    const transformedCategories = (categoriesData?.categories || []).map(cat => ({
      ...cat,
      translations: cat.category_translations?.map(translation => ({
        lanId: translation.language_id,
        lanName: translation.language_id === 1 ? 'العربية' :
                translation.language_id === 2 ? 'English' : 'کوردی',
        name: translation.name_text
      })) || [],
      image: cat.image_url
    }));
    setTempSortedList(transformedCategories);
    setHasUnsavedChanges(false);
  };

  // Show loading state
  if (isLoadingCategories) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading categories...</div>
      </div>
    );
  }

  // Show error state
  if (categoriesError) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        <div>Error loading categories: {categoriesError.message}</div>
      </div>
    );
  }

  return (
    <div className="categories-page-container">
      {/* Material Design Linear Progress Bar */}
      {isFetchingCategories && !isLoadingCategories && (
        <>
          <style>{`
            @keyframes indeterminate {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(400%);
              }
            }
            @keyframes indeterminate-short {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(300%);
              }
            }
          `}</style>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            zIndex: 9999,
            backgroundColor: '#e0e0e0',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '25%',
              backgroundColor: '#1890ff',
              animation: 'indeterminate 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite',
            }} />
            <div style={{
              position: 'absolute',
              height: '100%',
              width: '25%',
              backgroundColor: '#1890ff',
              animation: 'indeterminate-short 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
              animationDelay: '1.15s',
            }} />
          </div>
        </>
      )}

      {/* Header Section */}
      <div className="categories-header-section">
        <Row
          className="categories-header-row"
          justify="space-between"
          align="middle"
          gutter={[16, 16]}
        >
          <Col xs={24} sm={12} md={14}>
            <Title level={4} className="categories-title">
              Available Categories: ({categoriesData?.total || 0})
            </Title>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <Row gutter={[8, 8]} justify={isMobile ? 'center' : 'end'}>
              <Col xs={24} sm={sortMode ? 8 : 12} md={sortMode ? 6 : 8}>
                <Button
                  type="primary"
                  icon={<FolderAddOutlined />}
                  onClick={() => navigate('/category-create')}
                  className="categories-action-btn"
                  disabled={sortMode}
                >
                  {isMobile ? 'Add' : 'Add New Category'}
                </Button>
              </Col>
              {sortMode ? (
                <>
                  <Col xs={12} sm={8} md={6}>
                    <Button
                      type="dashed"
                      icon={<LeftOutlined />}
                      onClick={handleCancelSort}
                      className="categories-action-btn"
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Button
                      type="primary"
                      onClick={handleSaveSortOrder}
                      className="categories-action-btn"
                      disabled={!hasUnsavedChanges}
                    >
                      Save
                    </Button>
                  </Col>
                </>
              ) : (
                <Col xs={24} sm={12} md={8}>
                  <Button
                    type="primary"
                    icon={<DragOutlined />}
                    onClick={enterSortMode}
                    className="categories-action-btn"
                  >
                    Sort
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
        </Row>

        {/* Sort Mode Info */}
        {sortMode && (
          <div className="sort-mode-info">
            <Text type="secondary">
              🔄 Drag and drop categories to reorder. {hasUnsavedChanges && "Don't forget to save your changes!"}
            </Text>
          </div>
        )}
      </div>

      {/* Categories Grid */}
      <div className="categories-content-section">
        <Row
          gutter={[16, 16]}
          className={`categories-grid ${sortMode ? 'sort-mode' : ''}`}
        >
          <DndList
            list={sortMode ? tempSortedList : (categoriesData?.categories?.map(cat => ({
              ...cat,
              translations: cat.category_translations?.map(translation => ({
                lanId: translation.language_id,
                lanName: translation.language_id === 1 ? 'العربية' :
                        translation.language_id === 2 ? 'English' : 'کوردی',
                name: translation.name_text
              })) || [],
              image: cat.image_url
            })) || [])}
            onChange={handleSortChange}
            sortMode={sortMode}
            renderItem={cat => {
              const colProps = sortMode
                ? {
                  xs: 24,
                  sm: 12,
                  md: 8,
                  lg: 6,
                  style: { minWidth: isMobile ? '100%' : '220px' }
                }
                : {
                  xs: 24,
                  sm: 12,
                  md: 8,
                  lg: 6,
                  xl: 4
                };

              return (
                <Col {...colProps} key={cat.id}>
                  <Card
                    className={`category-card ${sortMode ? 'dragging' : ''}`}
                    cover={
                      <div className="category-image-wrapper">
                        <img
                          alt={`${cat.translations[0]?.name || 'category'} image`}
                          className="category-card-image"
                          src={cat.image}
                          onError={(e) => {
                            const fallbackUrl = getImageFallbackUrl(e.target.src);
                            // Check if we haven't tried the fallback yet
                            if (fallbackUrl !== e.target.src) {
                              e.target.src = fallbackUrl;
                            } else {
                              // Fallback also failed, show placeholder
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div className="category-image-placeholder" style={{ display: 'none' }}>
                          <span>No Image</span>
                        </div>
                      </div>
                    }
                    actions={sortMode ? [] : [
                      <Tooltip placement="top" title="Delete">
                        <DeleteOutlined
                          type="delete"
                          key="delete"
                          onClick={() => setSelectedItemToDelete(cat)}
                          className="category-action-icon"
                        />
                      </Tooltip>,
                      <Tooltip placement="top" title="Update">
                        <EditOutlined
                          type="edit"
                          key="edit"
                          onClick={() => navigate(`/category/${cat.id}`)}
                          className="category-action-icon"
                        />
                      </Tooltip>,
                    ]}
                  >
                    <Meta
                      title={
                        <div className="category-translations">
                          {cat.translations.map((translation, index) => (
                            <div key={index} className="translation-row">
                              <Text className="translation-text">
                                <span className="language-label">{translation.lanName}:</span>
                                <span className="translation-name">{translation.name}</span>
                              </Text>
                            </div>
                          ))}
                        </div>
                      }
                      description={
                        !sortMode && (
                          <Text type="secondary" className="products-count">
                            Category
                          </Text>
                        )
                      }
                    />
                    {sortMode && (
                      <div className="drag-indicator">
                        <DragOutlined />
                        <Text type="secondary">Drag to reorder</Text>
                      </div>
                    )}
                  </Card>
                </Col>
              );
            }}
          />
        </Row>
      </div>

      {/* Categories Pagination */}
      {categoriesData && categoriesData.pages > 1 && (
        <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}>
          <Pagination
            current={currentPage}
            total={categoriesData.total || 0}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} categories`}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete category"
        open={!!selectedItemDelete?.id}
        onOk={handleItemDelete}
        onCancel={() => setSelectedItemToDelete()}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete this category?
        </p>
        <p>
          <strong>Category:</strong> {selectedItemDelete?.category_translations?.[0]?.name_text || selectedItemDelete?.name || 'Unknown'}
        </p>
        <Text type="warning">⚠️ This action cannot be undone and may affect associated products.</Text>
      </Modal>
    </div>
  );
}

export default Categories;
